import { useEffect, useState } from "react";
import { Loader2, Save, Building2, Wallet, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { Switch } from "@/components/ui/switch";

const INPUT = "mt-1 h-11 w-full min-w-0 max-w-full box-border appearance-none rounded-xl border border-[#E1E1E6] bg-white px-3 text-[15px] focus:border-[#0071E3] focus:outline-none";

const Header = ({ icon: Icon, title, desc }) => (
  <div className="flex items-center gap-3">
    <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#EAF2FE] text-[#0071E3]"><Icon className="h-5 w-5" /></span>
    <div>
      <h1 className="text-[24px] font-semibold tracking-tight">{title}</h1>
      <p className="text-[13px] text-[#8E8E93]">{desc}</p>
    </div>
  </div>
);

const SaveBtn = ({ onClick, saving }) => {
  const { readOnly } = useAuth();
  const { t } = useT();
  if (readOnly) return null;
  return (
    <div className="flex justify-end">
      <button data-testid="settings-panel-save" onClick={onClick} disabled={saving}
        className="flex h-11 items-center gap-2 rounded-full bg-[#0071E3] px-6 text-[15px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.98] disabled:opacity-50">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t("pay.save")}
      </button>
    </div>
  );
};

function usePanel(path) {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get(path).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const save = async (body) => {
    setSaving(true);
    try { const { data } = await api.put(path, body); setData(data); toast.success(t("set.saved")); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  return { data, setData, saving, save };
}

export function CompanyProfilePanel() {
  const { t } = useT();
  const { readOnly } = useAuth();
  const { data, setData, saving, save } = usePanel("/settings/company");
  if (!data) return <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#B0B0B8]" /></div>;
  const f = (k) => (e) => setData({ ...data, [k]: e.target.value });
  return (
    <div className="w-full space-y-6">
      <Header icon={Building2} title={t("set.company")} desc={t("set.companyDesc")} />
      <div className="liquid-glass-card space-y-4 p-5 sm:p-6">
        <label className="block"><span className="text-[13px] font-medium">{t("set.companyName")}</span>
          <input data-testid="company-name" disabled={readOnly} className={INPUT} value={data.name} onChange={f("name")} /></label>
        <label className="block"><span className="text-[13px] font-medium">{t("set.companyBrand")}</span>
          <input data-testid="company-brand" disabled={readOnly} className={INPUT} value={data.brand} onChange={f("brand")} /></label>
        <label className="block"><span className="text-[13px] font-medium">{t("set.companyAddress")}</span>
          <input data-testid="company-address" disabled={readOnly} className={INPUT} value={data.address} onChange={f("address")} /></label>
        <label className="block"><span className="text-[13px] font-medium">{t("set.companyPhone")}</span>
          <input data-testid="company-phone" disabled={readOnly} className={INPUT} value={data.phone} onChange={f("phone")} /></label>
      </div>
      <SaveBtn saving={saving} onClick={() => save(data)} />
    </div>
  );
}

export function PayrollRulesPanel() {
  const { t } = useT();
  const { readOnly } = useAuth();
  const { data, setData, saving, save } = usePanel("/settings/payroll-rules");
  if (!data) return <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#B0B0B8]" /></div>;
  const num = (k) => (e) => setData({ ...data, [k]: Number(e.target.value) || 0 });
  return (
    <div className="w-full space-y-6">
      <Header icon={Wallet} title={t("set.payrollRules")} desc={t("set.rulesDesc")} />
      <div className="liquid-glass-card space-y-5 p-5 sm:p-6">
        <h2 className="text-[15px] font-semibold">{t("set.bpjsTitle")}</h2>
        <label className="block"><span className="text-[13px] font-medium">{t("set.bpjsAmount")}</span>
          <input data-testid="rules-bpjs" disabled={readOnly} type="number" className={INPUT} value={data.bpjs_amount} onChange={num("bpjs_amount")} /></label>
        <div className="h-px bg-black/[0.06]" />
        <h2 className="text-[15px] font-semibold">{t("set.bonusTitle")}</h2>
        <p className="text-[12px] text-[#8E8E93]">{t("set.bonusDesc")}</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block"><span className="text-[13px] font-medium">{t("set.bonusHighDays")}</span>
            <input data-testid="rules-high-days" disabled={readOnly} type="number" className={INPUT} value={data.bonus_high_days} onChange={num("bonus_high_days")} /></label>
          <label className="block"><span className="text-[13px] font-medium">{t("set.bonusHighAmount")}</span>
            <input data-testid="rules-high-amount" disabled={readOnly} type="number" className={INPUT} value={data.bonus_high_amount} onChange={num("bonus_high_amount")} /></label>
          <label className="block"><span className="text-[13px] font-medium">{t("set.bonusMidDays")}</span>
            <input data-testid="rules-mid-days" disabled={readOnly} type="number" className={INPUT} value={data.bonus_mid_days} onChange={num("bonus_mid_days")} /></label>
          <label className="block"><span className="text-[13px] font-medium">{t("set.bonusMidAmount")}</span>
            <input data-testid="rules-mid-amount" disabled={readOnly} type="number" className={INPUT} value={data.bonus_mid_amount} onChange={num("bonus_mid_amount")} /></label>
        </div>
      </div>
      <SaveBtn saving={saving} onClick={() => save(data)} />
    </div>
  );
}

export function WhatsAppTemplatePanel() {
  const { t } = useT();
  const { readOnly } = useAuth();
  const { data, setData, saving, save } = usePanel("/settings/wa-template");
  if (!data) return <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#B0B0B8]" /></div>;
  return (
    <div className="w-full space-y-6">
      <Header icon={MessageSquareText} title={t("set.waTemplate")} desc={t("set.waDesc")} />
      <div className="liquid-glass-card space-y-3 p-5 sm:p-6">
        <textarea data-testid="wa-template" disabled={readOnly} rows={7} value={data.template}
          onChange={(e) => setData({ ...data, template: e.target.value })}
          className="w-full rounded-xl border border-[#E1E1E6] bg-white p-3 text-[15px] leading-relaxed focus:border-[#0071E3] focus:outline-none" />
        <p className="text-[12px] text-[#8E8E93]">
          Placeholder: <code>{"{nama}"}</code> <code>{"{periode}"}</code> <code>{"{perusahaan}"}</code> <code>{"{nominal}"}</code> <code>{"{link}"}</code> — {t("set.waPlaceholderHint")}
        </p>
      </div>
      <SaveBtn saving={saving} onClick={() => save(data)} />
    </div>
  );
}
